import styles from '../../agents.module.css';

import clsxm from '@/lib/clsxm';

import type { DemoBrand } from '../types';
import { useChannelsT } from '../useChannelsT';
import { delay, tone } from '../../primitives';

/**
 * The lead form the website chat shows after the agent's first answer, and
 * the Leads list it fills.
 */
export default function LeadCapture({
  brand,
  question,
  answer,
  leads,
}: {
  brand: DemoBrand;
  question: string;
  answer: string;
  leads: { name: string; email: string; date: string }[];
}) {
  const { t } = useChannelsT();

  const fields = [t('Your Name'), t('Your Email'), t('Your Phone')];

  return (
    <div className='relative mx-auto max-w-[31rem] pb-2 sm:pb-6'>
      <div className='w-full overflow-hidden rounded-[18px] bg-white text-start shadow-[0_40px_90px_-30px_rgba(0,0,0,0.7)] ring-1 ring-white/10 sm:w-[17.5rem] dark:bg-[#1c1b27]'>
        <div
          className={clsxm(
            'flex items-center gap-2.5 px-3.5 py-3 text-white',
            brand.bg,
          )}
        >
          <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-white/20 text-[12px] font-semibold'>
            {brand.initial}
          </span>
          <span className='truncate text-[13px] font-semibold'>
            {brand.name}
          </span>
        </div>
        <div className='space-y-2 px-3 py-3 text-[12.5px] leading-snug'>
          <div className='flex justify-end'>
            <p
              className={clsxm(
                'max-w-[85%] rounded-2xl rounded-ee-md bg-[#f1f1f5] px-3 py-2 dark:bg-white/[0.08]',
                tone.ink,
              )}
            >
              {question}
            </p>
          </div>
          <p
            className={clsxm(
              'max-w-[88%] rounded-2xl rounded-es-md px-3 py-2 text-white',
              brand.bg,
            )}
          >
            {answer}
          </p>
          <div className='rounded-xl p-3 ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10'>
            <p className={clsxm('text-[12.5px] font-semibold', tone.ink)}>
              {t('Let us know how to contact you')}
            </p>
            <div className='mt-2 space-y-1.5'>
              {fields.map((field) => (
                <span
                  key={field}
                  className={clsxm(
                    'block rounded-md px-2.5 py-1.5 text-[11.5px] ring-1 ring-inset ring-[#0f0f28]/10 dark:ring-white/10',
                    tone.quiet,
                  )}
                >
                  {field}
                </span>
              ))}
            </div>
            <span
              className={clsxm(
                'mt-2.5 flex justify-center rounded-md py-1.5 text-[12px] font-semibold text-white',
                brand.bg,
              )}
            >
              {t('Submit')}
            </span>
          </div>
        </div>
      </div>

      <div
        className={clsxm(
          styles.rv,
          'relative mt-4 overflow-hidden rounded-2xl text-start shadow-[0_30px_60px_-24px_rgba(0,0,0,0.65)] sm:absolute sm:bottom-8 sm:end-0 sm:mt-0 sm:w-[17rem]',
          tone.surface,
          tone.line,
        )}
        style={delay(450)}
      >
        <p
          className={clsxm(
            'border-b border-black/[0.06] px-4 py-3 text-[13px] font-semibold dark:border-white/[0.06]',
            tone.ink,
          )}
        >
          {t('Leads')}
        </p>
        <ul className='divide-y divide-black/[0.05] dark:divide-white/[0.06]'>
          {leads.map(({ name, email, date }) => (
            <li key={email} className='flex items-center gap-3 px-4 py-2.5'>
              <span className='flex h-7 w-7 flex-none items-center justify-center rounded-full bg-[#f1f1f5] text-[11px] font-semibold text-[#56566d] dark:bg-white/10 dark:text-[#a4a4bd]'>
                {name.charAt(0)}
              </span>
              <span className='min-w-0 flex-1'>
                <span
                  className={clsxm(
                    'block truncate text-[12.5px] font-semibold',
                    tone.ink,
                  )}
                >
                  {name}
                </span>
                <span
                  className={clsxm('block truncate text-[11px]', tone.quiet)}
                >
                  <span dir='ltr'>{email}</span>
                </span>
              </span>
              <span
                className={clsxm(
                  'flex-none text-[11px] tabular-nums',
                  tone.quiet,
                )}
              >
                {date}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
